import { ContentDelete, NoticeLoading } from '@fs/zion-icon'
import {
  BillboardButton,
  Button,
  DangerButton,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Divider,
  Paragraph,
  useOverlay,
} from '@fs/zion-ui'

import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  generateSls,
  getNestedOrFloatingElementById,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { errorCollector } from '../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { removeFieldOrEvent } from './utils/eventUtils'
import RowWrapper from '../uiHelpers/RowWrapper'
import FieldWrapper from '../uiHelpers/FieldWrapper'
import { headers } from '../../../../../shared/services/config'

/**
 * DeleteEventButton component allows users to delete an event and optionally save the changes to SLS.
 *
 * @param {Object} params - The properties object.
 * @param {string} params.fieldLabel - The label of the field associated with the event.
 * @param {string} params.personId - The ID of the person associated with the event.
 * @param {Object} params.event - The event object to be deleted. This can be the SLS object or the event object from the form.
 * @param {Function} [params.onSuccess] - Callback function to be called upon successful deletion of the event.
 * @param {Function} [params.onError] - Callback function to be called if an error occurs during deletion.
 * @param {boolean} [params.shouldSaveToSls=true] - Flag indicating whether the changes should be saved to SLS.
 * @param {string} params.appName - We have many apps.  Needs to be the right one who is using this component.  E.g. RII
 * @returns {JSX.Element} The rendered component.
 */
const DeleteEventButton = ({
  fieldLabel,
  personId,
  event,
  onSuccess = () => {},
  onError = () => {},
  shouldSaveToSls = true,
  appName,
}) => {
  const [t] = useTranslation()
  const { images } = useSlsStateContext()
  const dispatch = useSlsDispatchContext()
  const deleteImpedanceOverlay = useOverlay()
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const onSuccessHandler = useCallback(
    (dispatches, sls) => {
      onSuccess(dispatches, sls)
      setSaving(false)
      deleteImpedanceOverlay.close()
    },
    [deleteImpedanceOverlay, onSuccess]
  )

  const deleteEvent = useCallback(async () => {
    setSaving(true)
    const parsedElement = getNestedOrFloatingElementById(images, event.id)
    const dispatches = parsedElement ? removeFieldOrEvent({ key: event.id, el: parsedElement }) : []
    const sls = dispatches ? generateSls(dispatches) : []
    if (shouldSaveToSls && parsedElement) {
      await saveSlsAndUpdateState({
        sls,
        dispatches,
        saveChangesFlag,
        slsActionsSplunkDebugger,
        userId: personId,
        dispatch,
        appName,
        fsUserAgentChain: headers['FS-User-Agent-Chain'],
        successHandler: () => {
          onSuccessHandler(dispatches, sls)
        },
        errorHandler: (err) => {
          setError(err)
          errorCollector(err)
          onError(err)
          setSaving(false)
        },
        saveInitiatorFilename: 'DeleteEventButton.js',
      })
    } else {
      onSuccessHandler(dispatches, sls)
    }
  }, [
    dispatch,
    personId,
    saveChangesFlag,
    slsActionsSplunkDebugger,
    shouldSaveToSls,
    event,
    onError,
    images,
    onSuccessHandler,
    appName,
  ])

  return (
    <>
      <Divider />
      <RowWrapper alignY="bottom">
        <FieldWrapper>
          <BillboardButton
            data-testid="delete-event"
            Icon={ContentDelete}
            color="red"
            emphasis="low"
            onClick={deleteImpedanceOverlay.handleClick}
            linkName="Delete Event"
          >
            {t('edit-index.delete-event.button', 'Delete {event}', {
              event: fieldLabel || 'Event',
            })}
          </BillboardButton>
          <DialogOverlay
            {...deleteImpedanceOverlay}
            title={t('edit-events.delete-impedance.are-you-sure', 'Are you sure?')}
          >
            <Paragraph size="sm">
              {t(
                'edit-events.delete-impedance.no-restore',
                'Once the event is removed, you will not be able to restore the information.'
              )}{' '}
              {t('edit-events.delete-impedance.please-confirm', 'Please confirm this change.')}
            </Paragraph>
            <DialogOverlayFooter>
              <DialogOverlayActions>
                <Button
                  disabled={saving}
                  data-testid="delete-event-impedance-cancel"
                  linkName="Delete Event Cancel"
                  onClick={() => deleteImpedanceOverlay.close()}
                >
                  {t('edit-events.delete-impedance.cancel.btn', 'Cancel')}
                </Button>
                <DangerButton
                  disabled={saving}
                  data-testid="delete-event-impedance-confirm"
                  linkName="Delete Event Confirm"
                  Icon={saving ? NoticeLoading : ContentDelete}
                  onClick={() => {
                    deleteEvent()
                  }}
                >
                  {t('edit-events.delete-impedance.remove.btn', 'Remove {eventType}', { eventType: fieldLabel })}
                </DangerButton>
              </DialogOverlayActions>
            </DialogOverlayFooter>
          </DialogOverlay>
          <ErrorOverlay
            error={error}
            setError={setError}
            title={t('save-error-overlay.heading', 'Unable to Save Changes')}
            message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
          />
        </FieldWrapper>
      </RowWrapper>
    </>
  )
}

export default DeleteEventButton
